/**
 * Табличные примитивы виджетов «Потребности СНП» (US-04.9.1) — общий набор
 * для `SnpNeedsSection`, `SnpThemes` и `SnpVillages`.
 *
 * Компоновка по дизайну от 09.09.2026: вместо крупных цифр, бейджей и
 * стек-баров каждый виджет — карточка с зелёным заголовком, подписью-
 * единицей справа и таблицей «подпись · [бар] · доля · значение». Итоговые
 * показатели идут строками того же тела таблицы (`FootRow`): подпись
 * занимает текстовые колонки, значения выравниваются по правым — так
 * «Протяжённость ЛЭП / подстанций» встаёт под «ЛЭП» и «Подстанции».
 *
 * Числа — `Intl.NumberFormat('ru-RU')` + `tabular-nums`; `null` = «данных
 * нет» и печатается как «—» (виджет остаётся с заголовком, FR-1.4).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { TableCell, TableHead, TableRow } from '../../../components/ui/table';
import { cn } from '../../../lib/cn';
import type { SnpCount } from '../../../types/snpNeeds';

export const fmt = (v: number): string =>
  Intl.NumberFormat('ru-RU').format(v);

export const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

/**
 * Доля в подписи: целые проценты, но доли меньше процента (привозная
 * вода — 0,1%) не должны схлопываться в «0%».
 */
export const fmtPct = (pct: number): string =>
  pct > 0 && pct < 1 ? `${fmt1(pct)}%` : `${Math.round(pct)}%`;

/** Доля в %, знаменатель — ответившие по показателю; нет пары → null. */
export const sharePct = (
  present: number | null,
  answered: number | null,
): number | null =>
  present !== null && answered !== null && answered > 0
    ? (present / answered) * 100
    : null;

/**
 * Смысловой цвет значения: `danger` — проблема (дефицит, износ, «нет
 * связи»), `good` — закрытая потребность, `share` — доля (везде одним
 * акцентным цветом, как на дизайне), `muted` — второстепенное значение.
 */
export type Tone = 'plain' | 'danger' | 'good' | 'share' | 'muted';

const TONE: Record<Tone, string> = {
  plain: 'text-text',
  danger: 'text-status-danger',
  good: 'text-brand-600',
  share: 'text-chart-indigo',
  muted: 'font-normal text-text-muted',
};

/** Шапка колонки: мелкая серая, подписи прижаты к низу. */
export const HEAD_TEXT =
  'h-auto px-0 py-1.5 align-bottom text-xs font-normal text-text-faint';
/** Числовая шапка не переносится: «Кол-во сёл» ломалось на две строки. */
export const HEAD_NUM = cn(HEAD_TEXT, 'whitespace-nowrap pl-4 text-right');

const CELL_TEXT = 'px-0 py-2 align-middle text-sm';
const CELL_NUM = cn(CELL_TEXT, 'pl-4 text-right font-bold tabular-nums');

/** Строка таблицы виджета (границы и ховер — токенами темы). */
export const ROW = 'border-surface-border hover:bg-surface-muted/40';

interface WidgetCardProps {
  title: string;
  testId: string;
  children: ReactNode;
}

/**
 * Карточка виджета: только зелёный заголовок и содержимое. Подписи-единицы
 * в шапке («сёл из 594», «человек») убраны — единицы измерения несут
 * шапки колонок и сами значения.
 */
export const WidgetCard = ({ title, testId, children }: WidgetCardProps) => (
  <Card className="shadow-card" data-testid={testId}>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

/** Шапка колонки: `num` — числовая (вправо). */
export const HeadCell = ({
  children,
  num = false,
  className,
  colSpan,
}: {
  children?: ReactNode;
  num?: boolean;
  className?: string;
  colSpan?: number;
}) => (
  <TableHead className={cn(num ? HEAD_NUM : HEAD_TEXT, className)} colSpan={colSpan}>
    {children}
  </TableHead>
);

/** Текстовая ячейка (подпись показателя). */
export const TextCell = ({
  children,
  className,
  colSpan,
}: {
  children?: ReactNode;
  className?: string;
  colSpan?: number;
}) => (
  <TableCell className={cn(CELL_TEXT, className)} colSpan={colSpan}>
    {children}
  </TableCell>
);

/** Числовая ячейка; `null` → «—» серым (данных нет). */
export const ValueCell = ({
  value,
  tone = 'plain',
  className,
  colSpan,
}: {
  value: ReactNode;
  tone?: Tone;
  className?: string;
  colSpan?: number;
}) => (
  <TableCell
    className={cn(
      CELL_NUM,
      value === null || value === undefined
        ? 'font-normal text-text-faint'
        : TONE[tone],
      className,
    )}
    colSpan={colSpan}
  >
    {value ?? '—'}
  </TableCell>
);

/** Ячейка доли: единый акцентный цвет, целые проценты (см. `fmtPct`). */
export const ShareCell = ({
  pct,
  className,
}: {
  pct: number | null;
  className?: string;
}) => (
  <ValueCell
    tone="share"
    className={className}
    value={pct !== null ? fmtPct(pct) : null}
  />
);

/** Ячейка с баром доли — визуальный ряд таблицы, значения в колонках. */
export const BarCell = ({ pct }: { pct: number | null }) => (
  <TableCell className={cn(CELL_TEXT, 'w-[42%] pl-4')}>
    <span
      className="block h-2 w-full overflow-hidden rounded-full bg-surface-muted"
      aria-hidden="true"
    >
      <span
        className="block h-full rounded-full bg-chart-indigo"
        style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }}
      />
    </span>
  </TableCell>
);

/**
 * Строка-итог: подпись занимает текстовые колонки, значения встают в
 * правые (`cols` — всего колонок таблицы). Строка не рендерится, если
 * ни одного значения нет — блок не показывает пустых итогов.
 */
export const FootRow = ({
  label,
  values,
  cols,
  testId,
}: {
  label: string;
  values: Array<{ value: ReactNode; tone?: Tone }>;
  cols: number;
  testId?: string;
}) => {
  if (values.every((item) => item.value === null)) return null;
  return (
    <TableRow className={ROW} data-testid={testId}>
      <TextCell
        colSpan={Math.max(1, cols - values.length)}
        className="text-text-muted"
      >
        {label}
      </TextCell>
      {values.map((item, index) => (
        <ValueCell
          // Колонки итога фиксированы по позиции — индекс здесь стабилен.
          key={index}
          value={item.value}
          tone={item.tone}
        />
      ))}
    </TableRow>
  );
};

/**
 * Подпись количества по паре показателя: знаменатель у каждого показателя
 * свой (графы «Мониторинга СНП» заполнены неравномерно — автобусное
 * сообщение заполнили 45 сёл из 585). Пока знаменатель совпадает с
 * заявленным в шапке виджета (`base`), печатается одно число; отличается —
 * «N из M», иначе доля вводила бы в заблуждение.
 */
export const useCountLabel = () => {
  const { t } = useTranslation();
  return (
    count: SnpCount | null | undefined,
    base: number | null,
  ): string | null => {
    if (count?.present == null) return null;
    const answered = count.answered;
    return answered !== null && answered !== base
      ? t('analytics.snpNeeds.ofVillages', {
          n: fmt(count.present),
          m: fmt(answered),
        })
      : fmt(count.present);
  };
};

/** Ячейка количества сёл по паре показателя (см. `useCountLabel`). */
export const CountCell = ({
  count,
  base,
  tone = 'plain',
}: {
  count: SnpCount | null | undefined;
  base: number | null;
  tone?: Tone;
}) => {
  const countLabel = useCountLabel();
  return <ValueCell value={countLabel(count, base)} tone={tone} />;
};

/** Подпись «нет данных» внутри карточки виджета (FR-1.4). */
export const NoData = ({ text }: { text: string }) => (
  <p
    data-testid="block-placeholder"
    className="text-sm lowercase text-text-muted"
  >
    {text}
  </p>
);
